import React, { useEffect, useState } from "react";
import { X, UserPlus, Trash2, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Modale « Gérer l'équipe » — permet à l'admin PRINCIPAL d'inviter des
 * co-admins qui partageront les MÊMES données (tournois, officiels, paies…).
 *
 * Les co-admins voient bien la liste mais ne peuvent PAS ajouter/retirer de
 * membres (backend renvoie 403 dans ce cas, le bouton est simplement masqué).
 */
export default function TeamManagementModal({ currentUserId, onClose }) {
  const [members, setMembers] = useState([]);
  const [teamOwnerId, setTeamOwnerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/members");
      setMembers(data.members || []);
      setTeamOwnerId(data.team_owner_id || null);
    } catch (err) {
      toast.error(formatApiError(err) || "Impossible de charger l'équipe");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isPrimary = currentUserId && teamOwnerId && currentUserId === teamOwnerId;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Email et mot de passe requis");
      return;
    }
    setSaving(true);
    try {
      await api.post("/team/members", form);
      toast.success("Co-admin ajouté avec succès");
      setForm({ email: "", password: "", name: "" });
      setShowAdd(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Ajout impossible");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (uid, email) => {
    if (!window.confirm(`Retirer ${email} de l'équipe ?`)) return;
    try {
      await api.delete(`/team/members/${uid}`);
      toast.success("Membre retiré");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Retrait impossible");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="team-modal-backdrop"
    >
      <div
        className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden mt-16 mb-8"
        style={{ background: "var(--bg)", border: "1px solid var(--border-strong)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="team-modal"
      >
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <div>
            <div className="label !mb-0">Équipe</div>
            <div className="text-lg font-semibold">Gérer les co-administrateurs</div>
          </div>
          <button
            onClick={onClose}
            className="btn-secondary !px-2.5 !py-2"
            data-testid="team-modal-close"
          >
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>

        <div className="px-5 py-4">
          <p className="text-xs text-[var(--muted)] font-mono leading-relaxed mb-4">
            Les co-admins partagent l&apos;ACCÈS aux mêmes tournois, officiels et
            paies. Chacun se connecte avec son propre email pour éviter les
            écrasements silencieux.
          </p>

          {loading ? (
            <div className="text-sm text-[var(--muted)]">Chargement…</div>
          ) : (
            <>
              <div className="divide-y" style={{ borderColor: "var(--border)" }} data-testid="team-members-list">
                {members.map((m) => (
                  <div
                    key={m.id}
                    className="py-3 flex items-center justify-between gap-3"
                    data-testid={`team-member-${m.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {m.is_primary && (
                        <Crown size={14} className="text-black flex-shrink-0" strokeWidth={2.2} title="Admin principal" />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm truncate">
                          {m.name || m.email}
                          {m.id === currentUserId && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--muted)]">
                              (toi)
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[var(--muted)] truncate">
                          {m.email}
                          {m.is_primary ? " · Admin principal" : " · Co-admin"}
                        </div>
                      </div>
                    </div>
                    {isPrimary && !m.is_primary && m.id !== currentUserId && (
                      <button
                        type="button"
                        onClick={() => handleDelete(m.id, m.email)}
                        className="btn-secondary !px-2.5 !py-2 text-red-700"
                        title="Retirer de l'équipe"
                        data-testid={`team-member-remove-${m.id}`}
                      >
                        <Trash2 size={14} strokeWidth={2.2} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {isPrimary && !showAdd && (
                <button
                  type="button"
                  onClick={() => setShowAdd(true)}
                  className="btn-primary w-full mt-4 flex items-center justify-center gap-2"
                  data-testid="team-add-toggle"
                >
                  <UserPlus size={15} strokeWidth={2.2} />
                  Inviter un co-admin
                </button>
              )}

              {isPrimary && showAdd && (
                <form onSubmit={handleAdd} className="mt-4 space-y-3 rounded-xl p-4" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }} data-testid="team-add-form">
                  <div>
                    <label className="label block mb-1">Nom (optionnel)</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="input-box"
                      placeholder="ex : Marie Tremblay"
                      data-testid="team-add-name"
                    />
                  </div>
                  <div>
                    <label className="label block mb-1">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input-box"
                      placeholder="coequipier@email.com"
                      data-testid="team-add-email"
                    />
                  </div>
                  <div>
                    <label className="label block mb-1">Mot de passe initial</label>
                    <input
                      type="text"
                      required
                      minLength={12}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="input-box"
                      placeholder="12+ caractères, 3 classes"
                      data-testid="team-add-password"
                    />
                    <div className="text-[10px] text-[var(--muted)] mt-1 font-mono">
                      Le co-admin pourra le changer une fois connecté.
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="btn-primary flex-1"
                      data-testid="team-add-submit"
                    >
                      {saving ? "Ajout…" : "Créer le co-admin"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAdd(false); setForm({ email: "", password: "", name: "" }); }}
                      className="btn-secondary"
                      data-testid="team-add-cancel"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}

              {!isPrimary && (
                <p className="text-[11px] text-[var(--muted)] font-mono mt-4">
                  Seul l&apos;admin principal peut ajouter ou retirer des membres.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
